<template>
  <schema-converter-wrapper
    data-qa="offerCreationForm"
    :legacy-schema="processedSchema"
    :initial-form-data="vacancyRequest.values"
    hide-loader
  >
    <template #default="{ schema, uiSchema, formData, loading: converterLoading }">
      <popup-layout :loading="loading || converterLoading" :title="vacancyRequest.position">
        <template #main>
          <slot name="main">
            <div data-qa="vacancy-request-view">
              <vjsf-form
                :schema="schema"
                :ui-schema="uiSchema"
                :form-data="formData"
                mode="inline"
              />

              <file-attachment-list
                v-if="files"
                :files="files"
                view-mode
                :class="$style.attachments"
              />
            </div>
          </slot>
        </template>
        <template #side>
          <div :class="$style.info">
            <watcher-info :vacancy-request="vacancyRequest" />
            <state
              v-for="(state, index) in states"
              :key="index"
              :first="index === 0"
              :state="state"
            />
            <more-button
              v-if="isShowMoreButtonVisible"
              :class="$style.moreButton"
              size="full"
              @click="showAllStates"
            >
              {{ $trlMessage('button.show_more') }}
            </more-button>
            <approval-summary v-if="isApproved" :vacancy-request="vacancyRequest" />
          </div>
        </template>
        <template #footer>
          <slot name="footer">
            <base-button kind="light" data-qa="cancel" @click="handleCancel">
              {{ $trlMessage('button.close') }}
            </base-button>
          </slot>
        </template>
      </popup-layout>
    </template>
  </schema-converter-wrapper>
</template>

<script>
import { computed, ref } from 'vue';

import { VacancyRequestStatus } from '@/types/vacancy-request-status';
import { userPermissions } from '@/services/config/user-permissions';
import BaseButton from '@/components/button/button.vue';
import PopupLayout from '@/components/popup-layout/popup-layout.vue';
import MoreButton from '@/components/more-button/more-button.vue';
import SchemaConverterWrapper from '@/components/vjsf/schema-converter-wrapper.vue';
import VjsfForm from '@/components/vjsf/vjsf-view.vue';
import FileAttachmentList from '@/components/ui/file-attachment-list/file-attachment-list.vue';
import ApprovalSummary from './approval-summary.vue';
import State from './state.vue';
import WatcherInfo from './watcher-info.vue';

const STATES_PAGE_LIMIT = 5;

export default {
  name: 'VacancyRequestView',
  components: {
    FileAttachmentList,
    SchemaConverterWrapper,
    VjsfForm,
    ApprovalSummary,
    BaseButton,
    PopupLayout,
    State,
    WatcherInfo,
    MoreButton
  },
  props: {
    vacancyRequest: {
      type: Object,
      default: null
    },
    vacancyRequestSchema: {
      type: Object,
      default: null
    },
    loading: Boolean
  },
  emits: ['cancel', 'more'],
  setup(props, { emit }) {
    const isAllStatesShown = ref(false);
    const showAllStates = () => {
      isAllStatesShown.value = true;
    };

    const isRejected = computed(
      () => props.vacancyRequest.status === VacancyRequestStatus.REJECTED
    );
    const isApproved = computed(
      () => props.vacancyRequest.status === VacancyRequestStatus.APPROVED
    );
    const states = computed(() => {
      const result = (props.vacancyRequest?.states || []).filter(
        (state) => !isRejected.value || state.status
      );
      if (isAllStatesShown.value) {
        return result;
      }
      return result.slice(0, STATES_PAGE_LIMIT);
    });
    const isShowMoreButtonVisible = computed(
      () => !isAllStatesShown.value && props.vacancyRequest?.states?.length > STATES_PAGE_LIMIT
    );

    const vacancyRequestIdRef = computed(() => {
      if (!isRejected.value) {
        return props.vacancyRequest.id;
      }
      // если заявка отклонена, то в этом компоненте нет её id-шника
      // (он оммитится для логики "отправить ещё раз"
      // но его можно получить из массива states
      const id = (props.vacancyRequest.states ?? []).find((state) => state.vacancy_request)
        ?.vacancy_request;
      return id;
    });

    const files = computed(() => props.vacancyRequest?.files || []);

    return {
      vacancyRequestId: vacancyRequestIdRef,
      canReceiveVacancyRequests: userPermissions.get('can_receive_vacancy_requests'),
      isWatcher: userPermissions.isWatcher,
      isRejected,
      isApproved,
      states,
      files,
      isShowMoreButtonVisible,
      showAllStates,
      handleCancel: () => emit('cancel')
    };
  },
  computed: {
    processedSchema() {
      if (!this.vacancyRequestSchema) {
        return this.vacancyRequestSchema;
      }
      // Выкидываем поле position, т.к. оно показывается в заголовке модалки
      const { position, ...schema } = this.vacancyRequestSchema.schema;
      return {
        ...this.vacancyRequestSchema,
        schema
      };
    }
  }
};
</script>

<style module>
.attachments {
  margin-left: -9px;
}

.info {
  overflow-wrap: break-word;
  word-break: normal;
}

.moreButton {
  padding-top: 10px;
}
</style>

<i18n lang="json" />
